import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import GridTable from "pages/_components/GridTable/GridTable";
import I18n from "pages/_components/I18n";
import Button from "pages/_components/Button";
import FormattedAmount from "pages/_components/FormattedAmount";
import CustomData from "pages/_components/CustomData/CustomData";
import Loader from "pages/_components/Loader";
import SummaryActions from "pages/echeck/components/SummaryActions";
import { findEmisor } from "util/echecks";
import { useGetAccounts } from "hooks/useAccounts";
import * as i18n from "util/i18n";
import { actions as notificationActions } from "reducers/notification";
import { actions as echeckActions } from "reducers/echecks";
import { goBack } from "connected-react-router";
import GeneralCredentialForm from "pages/_components/Forms/GeneralCredentialForm";
import { useConfirmTransaction } from "hooks/useConfirmTransaction";
import { types, EcheqActions } from "pages/echeck/constants";
import Notification from "pages/_components/Notification";

const ConfirmTransactionEcheckPage = () => {
    const { environmentCuit, echeqsToDoActions: echeqAction } = useSelector((state) => state.echecks);
    const { echeckTicket } = useSelector((state) => state.echecks);
    const { accounts } = useGetAccounts();
    const { dispatch } = useConfirmTransaction(echeqAction, echeckTicket, "echecks/action/transaction");
    const willCancel = echeqAction?.cancelTransaction;
    const isSubmitting = echeqAction?.isSubmitting || false;

    useEffect(() => {
        if (isEmpty(echeqAction)) {
            dispatch(goBack());
        }
    }, [dispatch]);

    const getTitleHeader = (echeqAction) => {
        if (echeqAction?.action === EcheqActions.ADMIT || echeqAction?.action === EcheqActions.REPUDIATE) {
            const echeq = echeqAction.echeqList[0];
            if (echeq.total_endosos > 0) {
                return `echecks.${echeqAction?.action?.toLowerCase()}.endorse`;
            }
        }

        return `echecks.${echeqAction?.action?.toLowerCase()}`;
    };

    const executeEcheqAction = () => {
        const { specialAttribute } = echeqAction;
        dispatch(echeckActions.executeActionEcheqTransaction({ ...echeqAction, specialAttribute }));
    };

    useEffect(() => {
        for (const echeq of echeqAction?.echeqList) {
            const { moneda } = echeq;
            const enabledAccounts = accounts.filter((account) => account.currency === moneda);

            const currency = { CURRENCY: i18n.get(`echeq.currency.${moneda}`) };

            // Mostramos snackbar solo para admitir echeq
            if (echeqAction.action == EcheqActions.ADMIT && enabledAccounts && enabledAccounts.length == 0) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("echeq.noAccountSameCurrency", null, currency),
                        "warning",
                        ["menu"],
                    ),
                );
            }
        }
    }, []);

    if (isEmpty(echeqAction)) {
        return <Loader />;
    }

    return (
        <>
            <Notification scopeToShow="/echecks/action/transaction/confirm" />
            <ResponsiveHeader id={willCancel ? "payments.cancel.title" : getTitleHeader(echeqAction)} />
            <MainContainer>
                {echeqAction?.echeqList?.length > 1 && <SummaryActions list={echeqAction?.echeqList} currency="ARS" />}
                <div className="container">
                    <I18n id="echecks.summary.confirm" component="h2" />
                    {echeqAction?.echeqList?.map((echeq) => {
                        const emisor = findEmisor(echeq, environmentCuit);

                        return (
                            <>
                                <GridTable className="form-summary">
                                    <GridTable.Container columns={3} rows={4}>
                                        <GridTable.Data columnStart={1} rowStart={1}>
                                            <CustomData
                                                titleText="echecks.number.label"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_numero}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        {echeqAction.action === EcheqActions.ANULATE_ENDORSE && (
                                            <>
                                                <GridTable.Data columnStart={2} rowStart={1}>
                                                    <CustomData
                                                        titleText="echecks.beneficiary"
                                                        titleTextType="I18N"
                                                        valueText={echeq.beneficiario_nombre}
                                                    />
                                                </GridTable.Data>
                                                <GridTable.Data columnStart={3} rowStart={1}>
                                                    <CustomData
                                                        titleText="echecks.document"
                                                        titleTextType="I18N"
                                                        valueText={`${echeq.beneficiario_documento_tipo} ${echeq.beneficiario_documento}`}
                                                    />
                                                </GridTable.Data>
                                            </>
                                        )}
                                        {echeqAction.action !== EcheqActions.ANULATE_ENDORSE && (
                                            <>
                                                <GridTable.Data columnStart={2} rowStart={1}>
                                                    <CustomData
                                                        titleText="echecks.emisor"
                                                        titleTextType="I18N"
                                                        valueText={emisor.emisorRazonSocial}
                                                        valueTextType="TEXT"
                                                    />
                                                </GridTable.Data>
                                                <GridTable.Data columnStart={3} rowStart={1}>
                                                    <CustomData
                                                        titleText="echecks.document"
                                                        titleTextType="I18N"
                                                        valueText={emisor.emisorDocumento}
                                                        valueTextType="TEXT"
                                                    />
                                                </GridTable.Data>
                                            </>
                                        )}

                                        <GridTable.Data columnStart={1} rowStart={2}>
                                            <CustomData
                                                titleText="echecks.amount"
                                                titleTextType="I18N"
                                                valueText={
                                                    <FormattedAmount
                                                        className="data-label"
                                                        currency={echeq.emisor_moneda}
                                                        quantity={echeq.monto}
                                                    />
                                                }
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} rowStart={2}>
                                            <CustomData
                                                titleText="echecks.id.label"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_id}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={3} rowStart={2}>
                                            <CustomData
                                                titleText="echecks.echeckType"
                                                titleTextType="I18N"
                                                valueText={echeq?.cheque_tipo}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={1} rowStart={3}>
                                            <CustomData
                                                titleText="echecks.currency"
                                                titleTextType="I18N"
                                                valueText={echeq.moneda}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} rowStart={3}>
                                            <CustomData
                                                titleText="echecks.createdRange.label"
                                                titleTextType="I18N"
                                                valueText={echeq.fecha_emision}
                                                valueTextType="DATE"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={3} rowStart={3}>
                                            <CustomData
                                                titleText="echecks.payedRange.label"
                                                titleTextType="I18N"
                                                valueText={echeq.fecha_pago}
                                                valueTextType="DATE"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={1} rowStart={4}>
                                            <CustomData
                                                titleText="echecks.echeckNature"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_caracter}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={2} rowStart={4}>
                                            <CustomData
                                                titleText="echecks.echeckMode"
                                                titleTextType="I18N"
                                                valueText={
                                                    echeq.cheque_modo === 1
                                                        ? "echecks.emit.cross"
                                                        : "echecks.emit.uncross"
                                                }
                                                valueTextType="I18N"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={3} rowStart={4}>
                                            <CustomData
                                                titleText="echecks.echeckConcept"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_concepto}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={1} rowStart={5}>
                                            <CustomData
                                                titleText="echecks.emit.motive.label"
                                                titleTextType="I18N"
                                                valueText={echeq.cheque_motivo_pago}
                                                valueTextType="TEXT"
                                            />
                                        </GridTable.Data>
                                        {echeqAction.action === EcheqActions.REPUDIATE && (
                                            <GridTable.Data columnStart={2} rowStart={5}>
                                                <CustomData
                                                    titleText="echeck.repudiate.label"
                                                    titleTextType="I18N"
                                                    valueText={echeqAction.specialAttribute}
                                                    valueTextType="TEXT"
                                                />
                                            </GridTable.Data>
                                        )}

                                        {echeqAction.action === EcheqActions.DEPOSIT && (
                                            <GridTable.Data columnStart={2} rowStart={5}>
                                                <CustomData
                                                    titleText="echeck.deposit.label"
                                                    titleTextType="I18N"
                                                    valueText={echeqAction?.accountDescription}
                                                    valueTextType="TEXT"
                                                />
                                            </GridTable.Data>
                                        )}
                                        {echeqAction.action === EcheqActions.CUSTODY && (
                                            <GridTable.Data columnStart={2} rowStart={5}>
                                                <CustomData
                                                    titleText="echeck.custody.label"
                                                    titleTextType="I18N"
                                                    valueText={echeqAction?.accountDescription}
                                                    valueTextType="TEXT"
                                                />
                                            </GridTable.Data>
                                        )}
                                        {echeqAction.action === EcheqActions.REQUEST_RETURN && (
                                            <GridTable.Data columnStart={2} rowStart={5}>
                                                <CustomData
                                                    titleText="echeck.custody.label"
                                                    titleTextType="I18N"
                                                    valueText={echeqAction?.accountDescription}
                                                    valueTextType="TEXT"
                                                />
                                            </GridTable.Data>
                                        )}
                                        {echeqAction.action === EcheqActions.ANULATE_ENDORSE && (
                                            <>
                                                <GridTable.Data columnStart={2} rowStart={5}>
                                                    <CustomData
                                                        titleText="echecks.endorse.date"
                                                        titleTextType="I18N"
                                                        valueText={echeq.endosos[0]?.fecha_hora}
                                                        valueTextType="DATE"
                                                    />
                                                </GridTable.Data>
                                                <GridTable.Data columnStart={3} rowStart={5}>
                                                    <CustomData
                                                        titleText="echecks.endorse.type.label"
                                                        titleTextType="I18N"
                                                        valueText={`echecks.endorse.types.${echeq.endosos[0]?.tipo_endoso}`}
                                                        valueTextType="I18N"
                                                    />
                                                </GridTable.Data>
                                                <GridTable.Data columnStart={1} rowStart={6}>
                                                    <CustomData
                                                        titleText="echeck.repudiate.label"
                                                        titleTextType="I18N"
                                                        valueText={echeqAction.specialAttribute}
                                                        valueTextType="TEXT"
                                                    />
                                                </GridTable.Data>
                                            </>
                                        )}
                                        {(echeqAction.action === EcheqActions.ANULATE_CESION ||
                                            echeqAction.action === EcheqActions.ADMIT_CESION ||
                                            echeqAction.action === EcheqActions.REPUDIATE_CESION) && (
                                            <>
                                                <GridTable.Data columnStart={2} rowStart={5}>
                                                    <CustomData
                                                        titleText="echecks.cession.dateTime"
                                                        titleTextType="I18N"
                                                        valueText={echeq.cesiones[0]?.fecha_emision_cesion}
                                                        valueTextType="DATE"
                                                    />
                                                </GridTable.Data>
                                                <GridTable.Data columnStart={3} rowStart={5}>
                                                    <CustomData
                                                        titleText="echecks.cession.beneficiaryAddress"
                                                        titleTextType="I18N"
                                                        valueText={echeq.cesiones[0]?.cesionario_domicilio}
                                                    />
                                                </GridTable.Data>
                                                <GridTable.Data columnStart={1} rowStart={6}>
                                                    <CustomData
                                                        titleText="echeck.repudiate.label"
                                                        titleTextType="I18N"
                                                        valueText={echeqAction.specialAttribute}
                                                        valueTextType="TEXT"
                                                    />
                                                </GridTable.Data>
                                            </>
                                        )}
                                    </GridTable.Container>
                                </GridTable>
                            </>
                        );
                    })}
                    {!willCancel && (
                        <GridTable>
                            <GridTable.Container columns={12} columnsTemplate="fixed" className="form-actions">
                                <GridTable.Data columnStart={5} columnWidth={4}>
                                    <div>
                                        {echeqAction.action === EcheqActions.RESCUE && (
                                            <CustomData
                                                icon="images/information.svg"
                                                titleText="echeck.rescue.hint"
                                                titleTextType="I18N"
                                            />
                                        )}

                                        {echeqAction.action === EcheqActions.DEPOSIT && (
                                            <CustomData
                                                icon="images/information.svg"
                                                titleText="echeck.deposit.hint"
                                                titleTextType="I18N"
                                            />
                                        )}
                                        {echeqAction.action === EcheqActions.CUSTODY && (
                                            <CustomData
                                                icon="images/information.svg"
                                                titleText="echeck.custody.hint"
                                                titleTextType="I18N"
                                            />
                                        )}
                                    </div>
                                </GridTable.Data>
                                <GridTable.Data columnStart={5} columnWidth={4}>
                                    <Button
                                        type="submit"
                                        bsStyle="primary"
                                        label="global.accept"
                                        onClick={executeEcheqAction}
                                        loading={isSubmitting}
                                    />
                                </GridTable.Data>
                            </GridTable.Container>
                        </GridTable>
                    )}
                    {willCancel && (
                        <GeneralCredentialForm
                            hideIndications
                            transactionData={{ echeqAction }}
                            action={echeckActions.cancelTransfer}
                            type="password"
                            idActivity="actions.echeqs.send"
                        />
                    )}
                </div>
            </MainContainer>
        </>
    );
};

export default ConfirmTransactionEcheckPage;
